import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import POLineItemTable from "@/components/POLineItemTable";
import { fmtDate, fmtINR, timeAgo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import {
  FileText, Clock, Factory, Package, Truck, AlertTriangle, CalendarClock, CheckCircle2,
  BellRing, ArrowRight, PhoneCall, Eye, Mail, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const PIE_COLORS = { green: "#22C55E", amber: "#F59E0B", red: "#EF4444", blue: "#3B82F6" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [supplierReqs, setSupplierReqs] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const load = () => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Failed to load dashboard"));
    api.get("/supplier-requests").then((r) => setSupplierReqs(r.data.requests.filter((x) => x.status !== "approved" && x.status !== "cancelled"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Loading control tower…</div>;
  const { kpis, attention, status_distribution, recent_pos, due_this_week } = data;

  const cards = [
    { label: "Active POs", value: kpis.total_active, icon: FileText, tone: "slate", id: "kpi-active" },
    { label: "Pending Acks", value: kpis.pending_ack, icon: Clock, tone: "amber", id: "kpi-pending-ack" },
    { label: "Manufacturing", value: kpis.manufacturing, icon: Factory, tone: "blue", id: "kpi-manufacturing" },
    { label: "Ready for Dispatch", value: kpis.ready_for_dispatch, icon: Package, tone: "blue", id: "kpi-ready" },
    { label: "In Transit", value: kpis.in_transit, icon: Truck, tone: "blue", id: "kpi-transit" },
    { label: "Overdue", value: kpis.overdue ?? 0, icon: AlertTriangle, tone: "red", id: "kpi-overdue" },
    { label: "Due This Week", value: kpis.due_this_week, icon: CalendarClock, tone: "amber", id: "kpi-due-week" },
    { label: "Delivered", value: kpis.delivered, icon: CheckCircle2, tone: "green", id: "kpi-delivered" },
    { label: "Active Enquiries", value: kpis.enquiries_active ?? 0, icon: Package, tone: "blue", id: "kpi-enquiries-active" },
    { label: "Draft Enquiries", value: kpis.enquiries_draft ?? 0, icon: FileText, tone: "slate", id: "kpi-enquiries-draft" },
    { label: "Awaiting Seller Response", value: kpis.enquiries_awaiting ?? 0, icon: Clock, tone: "amber", id: "kpi-enquiries-awaiting" },
    { label: "Closed Enquiries", value: kpis.enquiries_closed ?? 0, icon: CheckCircle2, tone: "green", id: "kpi-enquiries-closed" },
  ];

  const sendReminder = async (poId, channel = "whatsapp") => {
    try {
      await api.post(`/pos/${poId}/reminder`, { channel, template_key: "delivery_update_reminder" });
      toast.success("Reminder logged successfully");
      load();
    } catch (e) {
      toast.error("Could not send reminder");
    }
  };

  return (
    <div data-testid="buyer-dashboard" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="dashboard-greeting">{greeting}, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground">
            Here's what needs your attention today —{" "}
            {attention.length > 0
              ? <span data-testid="attention-summary"><span className="font-semibold text-[#DC2626]">{attention.filter(a => a.severity === "red").length} urgent</span> · <span className="font-semibold text-[#D97706]">{attention.length} POs need attention</span></span>
              : "all clear, nothing pending."}
          </p>
        </div>
        <Link to="/buyer/pos"><Button data-testid="goto-pos-btn" className="bg-slate-900 hover:bg-slate-800">All Purchase Orders <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c, i) => (
          <div key={c.id} className="animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <StatCard label={c.label} value={c.value} icon={c.icon} tone={c.tone} testId={c.id} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border bg-card lg:col-span-2">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <h2 className="font-heading text-base font-semibold">Requires Attention</h2>
            <span data-testid="attention-count" className="rounded-full bg-[#FEE2E2] px-2 py-0.5 text-xs font-bold text-[#991B1B]">{attention.length}</span>
          </div>
          <div className="divide-y">
            {attention.length === 0 && <p className="p-5 text-sm text-muted-foreground">No pending supplier actions. Great job.</p>}
            {attention.map((a, i) => (
              <div key={`${a.po_id}-${a.issue}`} data-testid={`attention-item-${i}`} className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-slate-50">
                <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${a.severity === "red" ? "bg-[#EF4444]" : "bg-[#F59E0B]"}`} />
                <div className="min-w-0 flex-1">
                  <button data-testid={`attention-po-${i}`} onClick={() => navigate(`/buyer/pos/${a.po_id}`)} className="font-mono text-sm font-semibold hover:underline">{a.po_number}</button>
                  <p className="truncate text-xs text-muted-foreground">{a.supplier_name} · {a.issue}{a.age_days > 0 ? ` · ${a.age_days}d pending` : ""}</p>
                </div>
                {a.cta === "Send Reminder" ? (
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" data-testid={`attention-wa-remind-${i}`} onClick={() => sendReminder(a.po_id, "whatsapp")}>
                      <MessageCircle className="mr-1 h-3.5 w-3.5 text-green-700" /> WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`attention-email-remind-${i}`} onClick={() => sendReminder(a.po_id, "email")}>
                      <Mail className="mr-1 h-3.5 w-3.5 text-blue-700" /> Email
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`attention-view-${i}`} onClick={() => navigate(`/buyer/pos/${a.po_id}`)}>View PO</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" data-testid={`attention-review-${i}`} onClick={() => navigate(`/buyer/pos/${a.po_id}`)}>
                    {a.cta === "Review Update" ? <Eye className="mr-1.5 h-3.5 w-3.5" /> : <PhoneCall className="mr-1.5 h-3.5 w-3.5" />} {a.cta}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">PO Status Mix</h2>
            <div className="h-56 min-h-56 w-full min-w-0 p-2" data-testid="status-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <PieChart>
                  <Pie data={status_distribution} dataKey="count" nameKey="label" innerRadius={50} outerRadius={75} paddingAngle={2}>
                    {status_distribution.map((s) => <Cell key={s.status} fill={PIE_COLORS[s.color] || "#94A3B8"} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-1 px-5 pb-4">
              {status_distribution.slice(0, 8).map((s) => (
                <div key={s.status} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[s.color] }} />
                  {s.label} <span className="ml-auto font-mono font-semibold text-slate-700">{s.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Due This Week</h2>
            <div className="divide-y">
              {due_this_week.length === 0 && <p className="p-5 text-sm text-muted-foreground">No deliveries due in the next 7 days.</p>}
              {due_this_week.map((p) => (
                <button key={p.id} data-testid={`due-${p.po_number}`} onClick={() => navigate(`/buyer/pos/${p.id}`)} className="flex w-full items-center justify-between px-5 py-2.5 text-left transition-colors hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-medium">{p.po_number}</p>
                    <p className="truncate text-xs text-muted-foreground">{p.supplier_name}</p>
                  </div>
                  <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-xs font-semibold text-[#92400E]">{fmtDate(p.expected_delivery_date)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {supplierReqs.length > 0 && (
        <div className="rounded-lg border bg-card" data-testid="supplier-requests-section">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <h2 className="font-heading text-base font-semibold">Supplier Requests</h2>
            <Link to="/buyer/suppliers" className="text-sm font-medium text-blue-700 hover:underline">View suppliers</Link>
          </div>
          <div className="divide-y">
            {supplierReqs.slice(0, 6).map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-5 py-3" data-testid={`supplier-req-${r.id.slice(0, 8)}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{r.supplier_name}</p>
                  <p className="text-xs text-muted-foreground">Requested {timeAgo(r.created_at)}{r.resolution_note ? ` · ${r.resolution_note}` : ""}</p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
                  {r.status === "pending" ? "Pending Enterprise Approval" : "Rejected"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="font-heading text-base font-semibold">Recently Updated POs</h2>
          <Link to="/buyer/pos" className="text-sm font-medium text-blue-700 hover:underline">View all</Link>
        </div>
        <div data-testid="recent-pos-table">
          <POLineItemTable pos={recent_pos} linkBase="/buyer/pos" testPrefix="recent-pos" rowTestPrefix="recent-po" />
        </div>
      </div>
    </div>
  );
}
